/**
 * Hub receiving labor wire pages. Canonical Postgres page is `receiving`;
 * job_key distinguishes Arrivals / Receive / Holding / To stock (and a specific order).
 */

export const RECEIVING_LABOR_STATION = "receiving";

export function receivingLaborWirePage(jobKey: string): string {
  return `receiving-job:${jobKey}`;
}

/** Strip the wire prefix to recover the Postgres `job_key` (e.g. `receive:purchase_order:12`). */
export function receivingLaborJobKeyFromWirePage(wirePage: string): string {
  const prefix = "receiving-job:";
  const trimmed = wirePage.trim();
  if (trimmed.startsWith(prefix)) {
    return trimmed.slice(prefix.length);
  }
  return trimmed;
}

export function inboundReceiveLaborPageKey(
  sourceType: "purchase_order" | "transfer_order" | null,
  sourceId: string,
): string {
  if (sourceType == null) return "";
  const parsed = Number.parseInt(sourceId, 10);
  if (!Number.isSafeInteger(parsed) || parsed <= 0) return "";
  return receivingLaborWirePage(`receive:${sourceType}:${String(parsed)}`);
}

/** Receive queue: browse-only, no labor clock in the account menu for this page key alone. */
export function isInboundReceivingBrowseOnlyLaborPage(pageKey: string): boolean {
  const key = pageKey.trim();
  return key === receivingLaborWirePage("receive");
}

/** Order receive workspace with an open dock crate (container-scoped labor). */
export function isInboundReceiveDetailLaborPage(pageKey: string): boolean {
  return /^receiving-job:receive:(purchase_order|transfer_order):\d+$/iu.test(pageKey.trim());
}

/**
 * Expected labor page for inbound floor and office URLs.
 * Returns null when the path is not an inbound receiving surface (caller falls through to shipping-team).
 * Discrepancy review is office work: hub-overview so leaving the dock clocks out of receiving.
 */
export function inboundLaborExpectedPageFromPathname(pathname: string): string | null {
  const path = pathname.replace(/\/+$/, "") || "/";

  if (
    path === "/shipping/receive/discrepancies"
    || path.startsWith("/shipping/inbound/discrepancies")
  ) {
    return receivingLaborWirePage("holding");
  }

  if (path === "/shipping/arrivals" || path.startsWith("/shipping/arrivals/")) {
    return receivingLaborWirePage("arrivals");
  }

  if (path === "/shipping/holding" || path.startsWith("/shipping/holding/")) {
    return receivingLaborWirePage("holding");
  }
  if (path === "/shipping/inbound/second-counts" || path.startsWith("/shipping/inbound/second-counts/")) {
    return receivingLaborWirePage("holding");
  }

  if (path === "/shipping/to-stock" || path.startsWith("/shipping/to-stock/")) {
    return receivingLaborWirePage("to-stock");
  }

  const receiveDetail = /^\/shipping\/receive\/(purchase_order|transfer_order)\/(\d+)/.exec(path);
  if (receiveDetail) {
    return inboundReceiveLaborPageKey(
      receiveDetail[1] as "purchase_order" | "transfer_order",
      receiveDetail[2] ?? "",
    );
  }
  const legacyDetail = /^\/shipping\/inbound\/(?:receive\/)?(purchase_order|transfer_order)\/(\d+)/.exec(path);
  if (legacyDetail) {
    return inboundReceiveLaborPageKey(
      legacyDetail[1] as "purchase_order" | "transfer_order",
      legacyDetail[2] ?? "",
    );
  }

  if (path === "/shipping/receive" || path === "/shipping/inbound" || path === "/shipping/inbound-v2") {
    return receivingLaborWirePage("receive");
  }

  return null;
}
